import { portalLocale, portalText } from "../../../i18n/portal.js";
/**
 * The words and figures of Finance → Allowances and caps and of the organization-wide concurrency copy (plan-014 E3b;
 * design §27.15.1; FR12-ENT-02/04/06/09, FR12-SCR-07). Pure — every sentence is computed from the API's facts, so the
 * suite pins them without a screen.
 *
 * Included talk time is counted in MINUTES, never euros (the prototype's lede); the cap is MONEY; the warning is a
 * PERCENTAGE that only notifies (FR12-ENT-06), always shown apart from what happens at the cap.
 */
import { ApiError } from "@wetalk/api-client";
import { money, type CurrencyCode } from "@wetalk/domain";

import { apiProblemText } from "../../../api/api-error.js";
import type {
  ConcurrencySourceView,
  EntitlementBalanceView,
  EntitlementGrantView,
  EntitlementWorkspaceView,
  SpendCapBehaviorView,
  SpendCapView,
} from "../../../api/slice/entitlement.js";
import { formatMoney } from "../../../money.js";
import { talkTimeText } from "../../../talk-time.js";

const SECOND_PER_MINUTE = 60;
const GROUPED = () => new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-GB"));
const SHORT_DATE = () => new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { day: "numeric", month: "short" });

export function eurText(minor: number, currency: string): string {
  return formatMoney(money(minor, currency as CurrencyCode));
}

/**
 * "3,118 min" — an allowance quantity (granted, allocated, unallocated) in whole minutes, never rounded up. Talk
 * time actually used is shown in minutes and seconds instead (`talkTimeText`, owner decision 2026-10-09).
 */
export function minuteText(second: number): string {
  return portalText("finance", "m0337", { value1: GROUPED().format(Math.floor(second / SECOND_PER_MINUTE)) });
}

function minuteCount(second: number): string {
  return GROUPED().format(Math.floor(second / SECOND_PER_MINUTE));
}

export interface StatView {
  readonly label: string;
  readonly value: string;
  readonly note: string;
}

/** The talk-second grants in force at `at` (included talk time; other kinds are resources, not minutes). */
export function talkGrantsAt(grants: readonly EntitlementGrantView[], at: Date): readonly EntitlementGrantView[] {
  return grants.filter(
    (one) =>
      one.kind === "talk_second" &&
      new Date(one.activeFrom).getTime() <= at.getTime() &&
      (one.activeUntil === null || new Date(one.activeUntil).getTime() > at.getTime()),
  );
}

/** "Trial minutes": "0 of 30 left" — the trial is used first (FR12-ENT-04). */
export function trialStatOf(balances: readonly EntitlementBalanceView[]): StatView {
  const granted = balances.reduce((sum, one) => sum + one.trialGrantedSecond, 0);
  const remaining = balances.reduce((sum, one) => sum + one.trialRemainingSecond, 0);
  if (granted === 0) return { label: portalText("finance", "m0338"), value: "—", note: portalText("finance", "m0339") };
  return {
    label: portalText("finance", "m0338"),
    value: portalText("finance", "m1260", { value1: minuteCount(remaining), value2: minuteCount(granted) }),
    note: remaining === 0 ? portalText("finance", "m0340") : portalText("finance", "m0341"),
  };
}

/** "Included minutes": "—, none on pay as you go" unless a plan grants talk time. */
export function includedStatOf(talk: readonly EntitlementGrantView[]): StatView {
  if (talk.length === 0) return { label: portalText("finance", "m0342"), value: "—", note: portalText("finance", "m0343") };
  const granted = talk.reduce((sum, one) => sum + one.grantedUnit, 0);
  const left = talk.reduce((sum, one) => sum + Math.max(0, one.grantedUnit - one.consumedUnit - one.reservedUnit), 0);
  const ends = talk.map((one) => one.activeUntil).filter((one): one is string => one !== null).sort();
  const first = ends[0];
  return {
    label: portalText("finance", "m0342"),
    value: portalText("finance", "m1260", { value1: minuteCount(left), value2: minuteCount(granted) }),
    note: first === undefined ? portalText("finance", "m0344") : portalText("finance", "m0345", { value1: SHORT_DATE().format(new Date(first)) }),
  };
}

/**
 * "Paid this month": the paid talk across every workspace in minutes and seconds, its money beside it — the
 * prototype's "3,118 min · €1,559.00", now "3,118 min 00 s" (INT 2026-10-07: the allowances read carries paid talk
 * as seconds and money; owner decision 2026-10-09: talk time is always minutes and seconds).
 */
export function paidStatOf(workspaces: readonly EntitlementWorkspaceView[], currency: string): StatView {
  const second = workspaces.reduce((sum, one) => sum + one.monthPaidTalk.second, 0);
  const minor = workspaces.reduce((sum, one) => sum + one.monthPaidTalk.minor, 0);
  return { label: portalText("finance", "m0346"), value: talkTimeText(second), note: eurText(minor, currency) };
}

/** "Unallocated": what a shared plan allowance has not given to any workspace — "applies to monthly plans" without one. */
export function unallocatedStatOf(talk: readonly EntitlementGrantView[]): StatView {
  if (talk.length === 0) return { label: portalText("finance", "m0347"), value: "—", note: portalText("finance", "m0348") };
  const left = talk.reduce((sum, one) => sum + Math.max(0, one.unallocatedUnit), 0);
  return { label: portalText("finance", "m0347"), value: minuteText(left), note: portalText("finance", "m0349") };
}

/** The order-of-use notice, in the prototype's words. */
export const COVERAGE_SEQUENCE_NOTICE = {
  get title() { return portalText("finance", "m1215"); },
  get text() { return portalText("finance", "m1216"); },
} as const;

/** A workspace's allocation from the shared talk-time grants ("—" for none). */
export function allocationTextOf(workspaceId: string, talk: readonly EntitlementGrantView[]): string {
  const allocated = talk.flatMap((one) => one.allocation).filter((one) => one.workspaceId === workspaceId);
  if (allocated.length === 0) return "—";
  return minuteText(allocated.reduce((sum, one) => sum + one.allocatedUnit, 0));
}

export function capTextOf(cap: SpendCapView | null): string {
  return cap === null ? portalText("finance", "m0350") : eurText(cap.monthBudgetMinor, cap.currency);
}

/** "€50" for a whole amount, "€12.50" otherwise — the prototype's "Allow €50 over". */
function shortEurText(minor: number, currency: string): string {
  return eurText(minor, currency).replace(/\.00$/, "");
}

/** What happens at the cap ("At the cap"). */
export function atCapTextOf(cap: SpendCapView | null): string {
  if (cap === null) return "—";
  switch (cap.capBehavior) {
    case "hard_stop":
      return portalText("finance", "m0351");
    case "capped_overage":
      return portalText("finance", "m0352", { value1: shortEurText(cap.overageMinor ?? 0, cap.currency) });
    case "notify_only":
      return portalText("finance", "m0353");
  }
}

/** "Warn at": a notification, never enforcement. */
export function warnAtTextOf(cap: SpendCapView | null): string {
  return cap === null ? "—" : `${String(cap.warnAtPercent)}%`;
}

/** The three behaviours as the cap form offers them. */
export const CAP_BEHAVIOR_CHOICE: readonly { readonly value: SpendCapBehaviorView; readonly label: string; readonly dek: string }[] = [
  { value: "hard_stop", get label() { return portalText("finance", "m0351"); }, get dek() { return portalText("finance", "m0354"); } },
  { value: "capped_overage", get label() { return portalText("finance", "m0355"); }, get dek() { return portalText("finance", "m0356"); } },
  { value: "notify_only", get label() { return portalText("finance", "m0353"); }, get dek() { return portalText("finance", "m0357"); } },
];

/** The cap's one-line summary on Workspace detail ("Monthly cap €2,000.00 — new calls stop at the cap. Warns at 80%."). */
export function capSummaryOf(cap: SpendCapView | null): string {
  if (cap === null) return portalText("finance", "m0358");
  const amount = eurText(cap.monthBudgetMinor, cap.currency);
  const behaviour =
    cap.capBehavior === "hard_stop"
      ? portalText("finance", "m0359")
      : cap.capBehavior === "capped_overage"
        ? portalText("finance", "m0360", { value1: shortEurText(cap.overageMinor ?? 0, cap.currency) })
        : portalText("finance", "m0361");
  return portalText("finance", "m0362", { value1: amount, value2: behaviour, value3: String(cap.warnAtPercent) });
}

/** "Your organization allows 25 at once, shared across every workspace and agent" (FR12-ENT-09). */
export function organizationConcurrencyText(allowed: number): string {
  return portalText("finance", "m0363", { value1: String(allowed) });
}

/** Where the concurrency entitlement comes from — the first source is the one that decides it. */
export function concurrencySourceTextOf(source: readonly ConcurrencySourceView[]): string {
  switch (source[0]) {
    case "quota_request":
      return portalText("finance", "m0364");
    case "time_bound_raise":
      return portalText("finance", "m0365");
    case "add_on":
      return portalText("finance", "m0366");
    case "revision":
      return portalText("finance", "m0367");
    default:
      return portalText("finance", "m0368");
  }
}

/** "no minutes included on this plan" is said only where it is TRUE: allowances read, no talk time granted now. */
export function noMinutesIncludedTrue(grants: readonly EntitlementGrantView[] | null, at: Date): boolean {
  return grants !== null && talkGrantsAt(grants, at).length === 0;
}

/**
 * A refused change, in plain words keyed by the API's STABLE code (design §27.9.2: `allocation_exceeds_grant`,
 * `allocation_below_use`; `revision_stale` on a stale If-Match). Any other refusal is the API's own sentence.
 */
export function entitlementRefusalTextOf(error: unknown, act: "cap" | "allocation"): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "revision_stale":
        return act === "cap"
          ? portalText("finance", "m0369")
          : portalText("finance", "m0370");
      case "allocation_exceeds_grant":
        return portalText("finance", "m0371");
      case "allocation_below_use":
        return portalText("finance", "m0372");
      case "impersonation_forbidden":
        return portalText("finance", "m0373");
      case "capability_missing":
      case "permission_denied":
        return act === "cap"
          ? portalText("finance", "m0374")
          : portalText("finance", "m0375");
      default:
        break;
    }
  }
  return apiProblemText(error, portalText("finance", "m0376"));
}

/** "2000", "€2,000.00", "12,5" → minor units; `null` for anything that is not a non-negative amount with ≤ 2 decimals. */
export function capAmountMinorOf(text: string): number | null {
  const trimmed = text.trim().replace(/^€\s*/, "").replace(/,(?=\d{3}(?:\D|$))/g, "");
  const match = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(trimmed);
  if (match === null) return null;
  const whole = Number(match[1]);
  const cents = match[2] === undefined ? 0 : Number(match[2].padEnd(2, "0"));
  return whole * 100 + cents;
}

/** A warning percentage 0–100, whole; `null` otherwise. */
export function warnPercentOf(text: string): number | null {
  const trimmed = text.trim().replace(/%$/, "").trim();
  if (!/^\d{1,3}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= 100 ? value : null;
}

/** Minor units as the cap field shows them ("2000.00"). */
export function capAmountFieldOf(minor: number): string {
  return `${String(Math.floor(minor / 100))}.${String(minor % 100).padStart(2, "0")}`;
}
